import React, { useState, useEffect } from "react";
import { Shield, Delete, Loader2, LogOut, Fingerprint, HelpCircle, ChevronLeft } from "lucide-react";
import { startAuthentication } from "@simplewebauthn/browser";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LockScreen = ({ onUnlock }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [digits, setDigits] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [hasPasskeys, setHasPasskeys] = useState(false);
  const [bioLoading, setBioLoading] = useState(false);

  // Forgot PIN state
  const [showForgotPin, setShowForgotPin] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpInput, setOtpInput] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");

  const firstName = user?.first_name || "User";

  useEffect(() => {
    // Check if user has a PIN set; if not, auto-unlock
    axios.get(`${API}/auth/pin-status`, { withCredentials: true })
      .then(({ data }) => {
        if (!data.has_pin) {
          sessionStorage.setItem("bompay_unlocked", "1");
          onUnlock();
        } else {
          setChecking(false);
          // Check if user has biometric registered
          axios.get(`${API}/webauthn/passkeys`, { withCredentials: true })
            .then(({ data: pk }) => setHasPasskeys((pk.passkeys || []).length > 0))
            .catch(() => {});
        }
      })
      .catch(() => {
        sessionStorage.setItem("bompay_unlocked", "1");
        onUnlock();
      });
  }, [onUnlock]);

  const handleDigit = (d) => {
    if (digits.length >= 4 || loading) return;
    setError("");
    const next = [...digits, d];
    setDigits(next);
    if (next.length === 4) verifyPin(next.join(""));
  };

  const handleDelete = () => {
    if (loading) return;
    setError("");
    setDigits(prev => prev.slice(0, -1));
  };

  const verifyPin = async (pin) => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-pin`, { pin }, { withCredentials: true });
      sessionStorage.setItem("bompay_unlocked", "1");
      onUnlock();
    } catch (e) {
      const msg = e.response?.data?.detail || "Incorrect PIN. Try again.";
      setError(msg);
      setDigits([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    sessionStorage.removeItem("bompay_unlocked");
    navigate("/appmobile");
  };

  const handleSendForgotOtp = async () => {
    setOtpLoading(true);
    setOtpError("");
    try {
      const { data } = await axios.post(`${API}/auth/forgot-pin`, {}, { withCredentials: true });
      setMaskedPhone(data.masked_phone || "");
      setOtpSent(true);
    } catch (e) {
      setOtpError(e.response?.data?.detail || "Failed to send code. Try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyForgotOtp = async () => {
    if (otpInput.length !== 6) { setOtpError("Enter the 6-digit code."); return; }
    setOtpLoading(true);
    setOtpError("");
    try {
      await axios.post(`${API}/auth/reset-pin-otp`, { otp_code: otpInput }, { withCredentials: true });
      // PIN cleared — auto-unlock (lock screen will detect no PIN and unlock)
      sessionStorage.setItem("bompay_unlocked", "1");
      onUnlock();
    } catch (e) {
      setOtpError(e.response?.data?.detail || "Invalid code. Try again.");
      setOtpInput("");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleBiometric = async () => {
    setBioLoading(true);
    setError("");
    try {
      const { data: opts } = await axios.post(`${API}/webauthn/auth/options`, {}, { withCredentials: true });
      const credential = await startAuthentication({ optionsJSON: opts });
      await axios.post(`${API}/webauthn/auth/verify`, { credential }, { withCredentials: true });
      sessionStorage.setItem("bompay_unlocked", "1");
      onUnlock();
    } catch (e) {
      if (e.name === "NotAllowedError") setError("Biometric cancelled. Use your PIN.");
      else setError(e.response?.data?.detail || "Biometric failed. Use your PIN.");
    } finally {
      setBioLoading(false);
    }
  };

  const pad = [
    [1, 2, 3],
    [4, 5, 6],
    [7, 8, 9],
    [null, 0, "del"],
  ];

  if (checking) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white" data-testid="lock-screen-loading">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-[#064BCB] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-white" data-testid="lock-screen">
      {/* Header */}
      <div className="flex flex-col items-center mb-10">
        <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center shadow-sm mb-4">
          <Shield size={28} className="text-[#064BCB]" />
        </div>
        <p className="text-[#064BCB] text-sm font-semibold tracking-widest uppercase mb-1">Bompay</p>
        <h2 className="text-gray-900 font-extrabold text-xl">Welcome back, {firstName}</h2>
        <p className="text-gray-500 text-sm mt-1">Enter your PIN to continue</p>
      </div>

      {/* PIN dots */}
      <div className="flex items-center justify-center gap-5 mb-4">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className={`w-4 h-4 rounded-full border-2 transition-all duration-150 ${
            i < digits.length
              ? "bg-[#064BCB] border-[#064BCB] scale-110"
              : "bg-gray-100 border-gray-300"
          }`} />
        ))}
      </div>

      {/* Error */}
      {error && (
        <p className="text-red-500 text-xs text-center mb-4 font-medium px-8" data-testid="lock-error">{error}</p>
      )}
      {!error && <div className="mb-4 h-4" />}

      {/* Numpad */}
      <div className="grid grid-cols-3 gap-3 w-64">
        {pad.map((row, ri) =>
          row.map((val, ci) => {
            if (val === null) return <div key={`${ri}-${ci}`} />;
            if (val === "del") return (
              <button
                key="del"
                onClick={handleDelete}
                disabled={loading}
                className="h-14 rounded-2xl flex items-center justify-center text-gray-500 hover:bg-gray-100 active:scale-95 transition-all"
                data-testid="lock-delete"
              >
                <Delete size={20} />
              </button>
            );
            return (
              <button
                key={val}
                onClick={() => handleDigit(String(val))}
                disabled={loading || digits.length >= 4}
                className="h-14 rounded-2xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 active:scale-95 border border-gray-200 font-bold text-xl text-gray-800 transition-all disabled:opacity-50"
                data-testid={`lock-key-${val}`}
              >
                {val}
              </button>
            );
          })
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center gap-2 mt-6 text-gray-500">
          <Loader2 size={16} className="animate-spin text-[#064BCB]" />
          <span className="text-sm">Verifying...</span>
        </div>
      )}

      {/* Biometric option */}
      {hasPasskeys && !loading && (
        <button
          onClick={handleBiometric}
          disabled={bioLoading}
          className="mt-6 flex items-center gap-2 bg-blue-50 hover:bg-blue-100 text-[#064BCB] px-5 py-3 rounded-2xl text-sm font-semibold transition-all border border-blue-100 disabled:opacity-50"
          data-testid="biometric-unlock-btn"
        >
          {bioLoading ? (
            <><Loader2 size={16} className="animate-spin" />Verifying biometric...</>
          ) : (
            <><Fingerprint size={18} />Use Fingerprint / Face ID</>
          )}
        </button>
      )}

      {/* Logout option */}
      <button
        onClick={handleLogout}
        className="mt-8 flex items-center gap-2 text-gray-400 hover:text-[#064BCB] text-sm transition-colors"
        data-testid="lock-logout"
      >
        <LogOut size={14} />
        Sign in as different user
      </button>

      {/* Forgot PIN */}
      {!showForgotPin ? (
        <button
          onClick={() => { setShowForgotPin(true); setError(""); }}
          className="mt-3 text-xs text-gray-400 hover:text-[#064BCB] transition-colors underline underline-offset-2"
          data-testid="forgot-pin-btn"
        >
          Forgot PIN?
        </button>
      ) : (
        <div className="mt-5 w-72 bg-blue-50 border border-blue-100 rounded-2xl p-4" data-testid="forgot-pin-panel">
          <div className="flex items-center gap-2 mb-3">
            <button onClick={() => { setShowForgotPin(false); setOtpSent(false); setOtpInput(""); setOtpError(""); }}
              className="text-gray-400 hover:text-gray-600">
              <ChevronLeft size={16} />
            </button>
            <p className="text-sm font-semibold text-gray-700">Reset your PIN</p>
          </div>
          {!otpSent ? (
            <>
              <p className="text-xs text-gray-500 mb-3">
                We'll send a 6-digit reset code to your registered phone number.
              </p>
              {otpError && <p className="text-xs text-red-500 mb-2">{otpError}</p>}
              <button
                onClick={handleSendForgotOtp}
                disabled={otpLoading}
                className="w-full bg-[#064BCB] text-white text-sm font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="send-pin-reset-otp-btn"
              >
                {otpLoading ? <><Loader2 size={14} className="animate-spin" />Sending...</> : "Send Reset Code"}
              </button>
            </>
          ) : (
            <>
              <p className="text-xs text-gray-500 mb-3">
                Enter the 6-digit code sent to <strong>{maskedPhone}</strong>
              </p>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otpInput}
                onChange={e => setOtpInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-center text-lg tracking-widest font-bold mb-2 focus:outline-none focus:border-[#064BCB]"
                data-testid="pin-reset-otp-input"
              />
              {otpError && <p className="text-xs text-red-500 mb-2">{otpError}</p>}
              <div className="flex gap-2">
                <button
                  onClick={() => { setOtpSent(false); setOtpInput(""); setOtpError(""); }}
                  className="flex-1 border border-gray-200 text-gray-600 text-sm font-semibold py-2 rounded-xl"
                >Resend</button>
                <button
                  onClick={handleVerifyForgotOtp}
                  disabled={otpLoading || otpInput.length !== 6}
                  className="flex-1 bg-[#064BCB] text-white text-sm font-semibold py-2 rounded-xl flex items-center justify-center gap-1 disabled:opacity-60"
                  data-testid="verify-pin-reset-otp-btn"
                >
                  {otpLoading ? <Loader2 size={14} className="animate-spin" /> : "Verify"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default LockScreen;
